import React from 'react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/core/utils';

interface ConfirmationDialogProps {
  trigger: React.ReactNode;
  title: string;
  description: string | React.ReactNode;
  confirmText: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel?: () => void;
  variant?: 'default' | 'destructive';
  requiresPassword?: boolean;
  password?: string;
  onPasswordChange?: (value: string) => void;
  loading?: boolean;
  disabled?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  trigger,
  title,
  description,
  confirmText,
  cancelText = "Cancel",
  onConfirm,
  onCancel,
  variant = 'default',
  requiresPassword = false,
  password = "",
  onPasswordChange,
  loading = false,
  disabled = false,
  open,
  onOpenChange
}) => (
  <AlertDialog open={open} onOpenChange={onOpenChange}>
    <AlertDialogTrigger asChild>
      {trigger}
    </AlertDialogTrigger>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle className={variant === 'destructive' ? 'text-destructive' : ''}>
          {title}
        </AlertDialogTitle>
        {typeof description === 'string' ? (
          <AlertDialogDescription className="space-y-4">
            {description}
            {requiresPassword && (
              <div className="mt-4 space-y-2">
                <Label htmlFor="password-confirm" className="text-sm font-medium">
                  Enter your password to confirm:
                </Label>
                <Input
                  id="password-confirm"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => onPasswordChange?.(e.target.value)}
                  className={cn(
                    variant === 'destructive' && "border-red-300 focus:border-red-500"
                  )}
                />
              </div>
            )}
          </AlertDialogDescription>
        ) : (
          <div className="space-y-4 text-sm text-muted-foreground">
            {description}
            {requiresPassword && (
              <div className="mt-4 space-y-2">
                <Label htmlFor="password-confirm" className="text-sm font-medium">
                  Enter your password to confirm:
                </Label>
                <Input
                  id="password-confirm"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => onPasswordChange?.(e.target.value)}
                  className={cn(
                    variant === 'destructive' && "border-red-300 focus:border-red-500"
                  )}
                />
              </div>
            )}
          </div>
        )}
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel 
          onClick={onCancel}
          disabled={loading}
        >
          {cancelText}
        </AlertDialogCancel>
        <AlertDialogAction
          onClick={(e) => {
            if (loading) {
              e.preventDefault();
              return;
            }
            onConfirm();
          }}
          disabled={disabled || (requiresPassword && !password.trim())}
          className={cn(
            variant === 'destructive' && "bg-red-600 hover:bg-red-700 disabled:bg-red-300",
            loading && "opacity-90"
          )}
        >
          {loading ? (
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              {confirmText.includes('...') ? confirmText : `${confirmText.split(' ')[0]}ing...`}
            </div>
          ) : (
            confirmText
          )}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);